import React, { useEffect, useState } from "react";
import EditModal from "@core/modals/EditModal";
import EditButton from "@core/buttons/atoms/EditButton";
import Input from "@core/inputs/Input";
import { showError, showSuccess } from "@utils/helpers";
import { useTranslation } from "react-i18next";
import PermissionHandler from "@hoc/cruds/PermissionHandler";
import CountryService from "@services/CountryService";

const EditCountry = ({ country, onEdited }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [englishName, setEnglishName] = useState("");
  const [frenchName, setFrenchName] = useState("");
  const [spanishName, setSpanishName] = useState("");
  const [unitedNation, setUnitedNation] = useState("");
  const [unitedNationDp, setUnitedNationDp] = useState("");
  const [iso2, setIso2] = useState("");
  const [iso3, setIso3] = useState("");
  const [gfcm, setGfcm] = useState(false);
  const [europeanUnion, setEuropeanUnion] = useState(false);
  const [medFisis, setMedfisis] = useState(false);
  const [description, setDescription] = useState("");

  const closeModal = () => {
    setShowModal(false);
    setName("");
    setEnglishName("");
    setSpanishName("");
    setFrenchName("");
    setUnitedNation("");
    setUnitedNationDp("");
    setIso2("");
    setIso3("");
    setGfcm("");
    setEuropeanUnion("");
    setMedfisis("");
    setDescription("");
  };

  const onSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    CountryService.update(
      country.id,
      name,
      englishName,
      frenchName,
      spanishName,
      description,
      unitedNation,
      unitedNationDp,
      iso2,
      iso3,
      gfcm,
      europeanUnion,
      medFisis
    )
      .then(() => {
        showSuccess(t("countryEditedSuccessfully"));
        onEdited();
        closeModal();
      })
      .catch((err) => {
        showError(err.response.data.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (showModal)
      CountryService.find(country.id).then((response) => {
        setName(response.data.data.name);
        setEnglishName(response.data.data.name_eng);
        setSpanishName(response.data.data.name_esp);
        setFrenchName(response.data.data.name_frn);
        setUnitedNation(response.data.data.un);
        setUnitedNationDp(response.data.data.undp);
        setIso2(response.data.data.iso_2);
        setIso3(response.data.data.iso_3);
        setGfcm(response.data.data.gfcm);
        setEuropeanUnion(response.data.data.eu);
        setMedfisis(response.data.data.med_fisis);
        setDescription(response.data.data.description);
      });
  }, [country.id, showModal]);

  return (
    <PermissionHandler permission={"update_country"}>
      <EditButton onClick={() => setShowModal(true)} />
      <EditModal
        show={showModal}
        title={t("editCountry")}
        onClose={closeModal}
        onSubmit={onSubmit}
        loading={loading}
        label={t("edit")}
      >
        <div className="mt-4 sm:mt-8 mb-2 grid grid-cols-2 gap-5">
          <Input
            height="h-10"
            label={t("name")}
            placeholder={t("name")}
            value={name}
            onChange={setName}
          />
          <Input
            height="h-10"
            label={t("nameEng")}
            placeholder={t("nameEng")}
            value={englishName}
            onChange={setEnglishName}
          />
          <Input
            height="h-10"
            label={t("nameFrn")}
            placeholder={t("nameFrn")}
            value={frenchName}
            onChange={setFrenchName}
          />
          <Input
            height="h-10"
            label={t("nameEsp")}
            placeholder={t("nameEsp")}
            value={spanishName}
            onChange={setSpanishName}
          />
          <Input
            height="h-10"
            label={t("unitedNation")}
            placeholder={t("unitedNation")}
            value={unitedNation}
            onChange={setUnitedNation}
          />
          <Input
            height="h-10"
            label={t("unitedNationDp")}
            placeholder={t("unitedNationDp")}
            value={unitedNationDp}
            onChange={setUnitedNationDp}
          />
          <Input
            height="h-10"
            label={t("iso2")}
            placeholder={t("iso2")}
            value={iso2}
            onChange={setIso2}
          />
          <Input
            height="h-10"
            label={t("iso3")}
            placeholder={t("iso3")}
            value={iso3}
            onChange={setIso3}
          />
          <Input
            height="h-10"
            label={t("description")}
            placeholder={t("description")}
            value={description}
            onChange={setDescription}
          />
          <div className="flex">
            <div className="flex items-center mt-5">
              <input
                type="checkbox"
                checked={gfcm}
                onClick={() => {
                  setGfcm(!gfcm);
                }}
                className="w-4 h-4 text-indigo-700 bg-gray-100 rounded border-gray-300 focus:ring-indigo-500 focus:ring-2"
              />
              <label className="ml-2 text-sm text-gray-700">{t("gfcm")}</label>
            </div>
            <div className="flex items-center mt-5 ml-3">
              <input
                type="checkbox"
                checked={europeanUnion}
                onClick={() => {
                  setEuropeanUnion(!europeanUnion);
                }}
                className="w-4 h-4 text-indigo-700 bg-gray-100 rounded border-gray-300 focus:ring-indigo-500 focus:ring-2"
              />
              <label className="ml-2 text-sm text-gray-700">
                {t("europeanUnion")}
              </label>
            </div>
            <div className="flex items-center mt-5 ml-5">
              <input
                type="checkbox"
                checked={medFisis}
                onClick={() => {
                  setMedfisis(!medFisis);
                }}
                className="w-4 h-4 text-indigo-700 bg-gray-100 rounded border-gray-300 focus:ring-indigo-500 focus:ring-2"
              />
              <label className="ml-2 text-sm text-gray-700">
                {t("medFisis")}
              </label>
            </div>
          </div>
        </div>
      </EditModal>
    </PermissionHandler>
  );
};

export default EditCountry;
